import React from 'react';
import { Link } from 'react-router-dom';

const ServicesPage = () => {

    // 1. THE PROCESS (The Pipeline)
    const processSteps = [
        {
            id: "01",
            title: "DISCOVERY & LOGIC",
            desc: "We decode your business requirements and map out the database architecture before writing a single line of code."
        },
        {
            id: "02",
            title: "AGILE DEVELOPMENT",
            desc: "Two-week sprints with live preview links. You see the product evolve in real-time. No black boxes."
        },
        {
            id: "03",
            title: "STRESS TESTING",
            desc: " rigorous QA. We test for load, security vulnerabilities (OWASP), and mobile responsiveness."
        },
        {
            id: "04",
            title: "DEPLOY & SCALE",
            desc: "CI/CD setup on Vercel/AWS. We hand over the keys, the documentation, and the IP."
        }
    ];

    // 2. THE SERVICES (The Modules)
    const services = [
        {
            title: "Custom SaaS Engines",
            price: "Starts at $3k",
            desc: "Full-stack applications with complex business logic. User dashboards, subscription management, and role-based access control.",
            features: ["MERN Stack Architecture", "Stripe Subscription API", "Admin Panels (RBAC)", "Real-time Notifications"],
            icon: "⚡️"
        },
        {
            title: "E-Commerce Ecosystems",
            price: "Starts at $2k",
            desc: "High-performance storefronts designed for conversion. We build headless Shopify or custom Node.js shops.",
            features: ["Next.js Frontends", "Inventory Synchronization", "Global Payment Gateways", "Cart Logic Optimization"],
            icon: "🛍️"
        },
        {
            title: "App Modernization",
            price: "Hourly / Retainer",
            desc: "Refactoring legacy codebases. We optimize slow React apps, fix security holes, and migrate databases.",
            features: ["Code Audits", "Performance Tuning", "Security Patching", "Database Migration"],
            icon: "🛠️"
        }
    ];

    // 3. THE ARSENAL (Tech Stack)
    const techStack = [
        "React.js", "Next.js", "Node.js", "Express",
        "MongoDB", "PostgreSQL", "Redis", "Docker",
        "Tailwind", "Framer", "Stripe", "AWS"
    ];

    return (
        <div className="min-h-screen bg-void text-white pt-24 pb-24 relative overflow-hidden">

            {/* Background Decor (Schematic Grid) */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

            {/* --- HERO SECTION --- */}
            <div className="container mx-auto px-6 mb-24 relative z-10">
                <div className="max-w-3xl">
                    <span className="text-neon-blue font-mono text-sm tracking-widest uppercase mb-4 block">
            // System Capabilities
                    </span>
                    <h1 className="text-5xl md:text-7xl font-black font-display text-white mb-6">
                        ENGINEERING <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-white">INFRASTRUCTURE</span>
                    </h1>
                    <p className="text-gray-400 text-lg leading-relaxed border-l-2 border-neon-blue pl-6">
                        We don't sell "websites." We sell digital assets that automate your business, process payments, and scale with your user base.
                    </p>
                </div>
            </div>

            {/* --- SECTION 1: THE PIPELINE (Process) --- */}
            <div className="bg-surface/30 border-y border-white/5 backdrop-blur-sm py-20 mb-24">
                <div className="container mx-auto px-6">
                    <h2 className="text-2xl font-bold text-white mb-12 flex items-center gap-3">
                        <span className="w-2 h-2 bg-neon-blue rounded-full"></span>
                        THE DEPLOYMENT PIPELINE
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
                        {/* Connecting Line (Desktop Only) */}
                        <div className="hidden md:block absolute top-8 left-0 w-full h-px bg-white/10 -z-10"></div>

                        {processSteps.map((step) => (
                            <div key={step.id} className="relative group">
                                {/* Number Circle */}
                                <div className="w-16 h-16 bg-void border border-white/20 rounded-full flex items-center justify-center font-mono text-neon-blue font-bold text-xl mb-6 group-hover:border-neon-blue group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                                    {step.id}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3 font-display">{step.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* --- SECTION 2: CORE MODULES (Services) --- */}
            <div className="container mx-auto px-6 mb-32 relative z-10">
                <h2 className="text-4xl md:text-5xl font-black text-white mb-12 font-display text-center">
                    CORE <span className="text-gray-600">MODULES</span>
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {services.map((service, index) => (
                        <div key={index} className="bg-surface border border-white/10 rounded-2xl p-8 hover:-translate-y-2 hover:border-neon-blue/40 transition-all duration-300 group">

                            {/* Header */}
                            <div className="flex justify-between items-start mb-6">
                                <div className="text-4xl">{service.icon}</div>
                                <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-neon-blue">
                                    {service.price}
                                </div>
                            </div>

                            <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                            <p className="text-gray-400 mb-8 leading-relaxed text-sm">
                                {service.desc}
                            </p>

                            {/* Features List */}
                            <div className="space-y-3 border-t border-white/5 pt-6">
                                {service.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                                        <svg className="w-4 h-4 text-neon-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        {feature}
                                    </div>
                                ))}
                            </div>

                            {/* Hover Glow */}
                            <div className="mt-8 pt-6 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Link to="/contact" className="text-neon-blue font-bold text-sm uppercase tracking-widest">
                                    Request Quote
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* --- SECTION 3: THE ARSENAL (Tech Stack) --- */}
            <div className="container mx-auto px-6 mb-24 relative z-10">
                <div className="bg-[#0B0B15] border border-white/10 rounded-3xl p-10 md:p-16 text-center overflow-hidden relative">

                    {/* Background Glow */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-600/10 blur-[100px] rounded-full pointer-events-none"></div>

                    <h2 className="text-3xl md:text-4xl font-black text-white mb-12 font-display relative z-10">
                        THE TECHNOLOGICAL <span className="text-purple-500">ARSENAL</span>
                    </h2>

                    {/* Grid of Tech */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 relative z-10">
                        {techStack.map((tech) => (
                            <div key={tech} className="p-4 bg-white/5 border border-white/5 rounded-lg text-gray-300 font-mono text-sm hover:bg-white/10 hover:text-white hover:border-purple-500/50 transition-all cursor-default">
                                {tech}
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-gray-500 text-sm font-mono">
                // SYSTEM STATUS: OPTIMIZED FOR PERFORMANCE
                    </p>
                </div>
            </div>

            {/* --- CTA SECTION --- */}
            <div className="text-center pb-12">
                <p className="text-gray-400 mb-6">Not sure which module fits your needs?</p>
                <a href="/contact" className="text-white border-b border-white pb-1 hover:text-neon-blue hover:border-neon-blue transition-colors text-lg">
                    Schedule a Systems Architecture Call →
                </a>
            </div>

        </div>
    );
};

export default ServicesPage;